/**
 * Meilisearch Search UI Module
 * Handles the search preview dropdown functionality
 */

import {
  isMeiliEnabled,
  getMeiliConfig,
  meiliSearch,
  formatPrice,
  escapeHtml,
  getProductUrl,
  getProductImageUrl,
  debounce,
  getSelectedFinish,
  getFinishPrice
} from "./meilisearch-client";
import type { MeiliProduct, MeiliSearchElements } from "../types/meilisearch";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_DELAY = 300;

/**
 * Get search preview element references
 */
function getSearchElements(previewContainer: HTMLElement): MeiliSearchElements | null {
  const results = previewContainer.querySelector<HTMLElement>("[data-meili-search-results]");
  const viewAll = previewContainer.querySelector<HTMLAnchorElement>("[data-meili-search-view-all]");
  const empty = previewContainer.querySelector<HTMLElement>("[data-meili-search-empty]");
  const loading = previewContainer.querySelector<HTMLElement>("[data-meili-search-loading]");

  if (!results || !viewAll || !empty || !loading) {
    console.warn("[Meilisearch] Missing search preview elements");
    return null;
  }

  return {
    container: previewContainer,
    results,
    viewAll,
    empty,
    loading
  };
}

/**
 * Show loading state
 */
function showLoading(elements: MeiliSearchElements): void {
  elements.loading.hidden = false;
  elements.results.hidden = true;
  elements.empty.hidden = true;
  elements.viewAll.hidden = true;
  elements.container.hidden = false;
}

/**
 * Show results
 */
function showResults(elements: MeiliSearchElements): void {
  elements.loading.hidden = true;
  elements.results.hidden = false;
  elements.empty.hidden = true;
  elements.viewAll.hidden = false;
  elements.container.hidden = false;
}

/**
 * Show empty state
 */
function showEmpty(elements: MeiliSearchElements): void {
  elements.loading.hidden = true;
  elements.results.hidden = true;
  elements.empty.hidden = false;
  elements.viewAll.hidden = true;
  elements.container.hidden = false;
}

/**
 * Hide preview
 */
function hidePreview(elements: MeiliSearchElements): void {
  elements.container.hidden = true;
}

/**
 * Render a single product result
 */
function renderProductItem(product: MeiliProduct, query: string): string {
  const url = getProductUrl(product.handle);
  const imageUrl = getProductImageUrl(product.image_url, product.images, "120x120");
  const title = escapeHtml(product.title);
  const sculptor = product.sculptor ? escapeHtml(product.sculptor) : "";

  // Get price based on selected finish
  const selectedFinish = getSelectedFinish();
  const finishPrice = getFinishPrice(product.variants, selectedFinish);
  // Use finish price if available, otherwise fallback to price_min or price
  const priceValue = finishPrice ?? product.price_min ?? product.price;
  const price = priceValue ? formatPrice(priceValue * 100) : ""; // prices are in euros, not cents

  // Highlight matching text in title
  const highlightedTitle = highlightMatch(title, query);

  return `
    <li class="meili-search-preview__item" role="option">
      <a href="${url}" class="meili-search-preview__link">
        <div class="meili-search-preview__image-wrapper">
          <img
            src="${imageUrl}"
            alt="${title}"
            class="meili-search-preview__image"
            loading="lazy"
            width="60"
            height="60"
          >
        </div>
        <div class="meili-search-preview__content">
          <span class="meili-search-preview__title">${highlightedTitle}</span>
          ${sculptor ? `<span class="meili-search-preview__sculptor">${sculptor}</span>` : ""}
          ${price ? `<span class="meili-search-preview__price">${price}</span>` : ""}
        </div>
      </a>
    </li>
  `;
}

/**
 * Highlight matching text
 */
function highlightMatch(text: string, query: string): string {
  if (!query) return text;

  const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(`(${escapedQuery})`, "gi");
  return text.replace(regex, "<mark>$1</mark>");
}

/**
 * Render search results
 */
function renderSearchResults(
  hits: MeiliProduct[],
  query: string,
  totalHits: number,
  elements: MeiliSearchElements
): void {
  if (hits.length === 0) {
    showEmpty(elements);
    return;
  }

  // Render product list
  const html = hits.map((product) => renderProductItem(product, query)).join("");
  elements.results.innerHTML = html;

  // Update view all link
  const viewAllText = elements.viewAll.querySelector("[data-meili-search-view-all-text]");
  if (viewAllText) {
    viewAllText.textContent = `View all ${totalHits} results`;
  }
  elements.viewAll.href = `/search?q=${encodeURIComponent(query)}`;

  showResults(elements);
}

/**
 * Perform search and update UI
 */
async function performSearch(query: string, elements: MeiliSearchElements): Promise<void> {
  const trimmedQuery = query.trim();
  console.log("[Meilisearch] Performing search for:", trimmedQuery);

  if (trimmedQuery.length < MIN_QUERY_LENGTH) {
    console.log("[Meilisearch] Query too short, hiding preview");
    hidePreview(elements);
    return;
  }

  showLoading(elements);

  const config = getMeiliConfig();
  console.log("[Meilisearch] Config:", config);
  const limit = config?.previewLimit ?? 6;

  const response = await meiliSearch({
    query: trimmedQuery,
    limit,
    attributesToRetrieve: [
      "id",
      "handle",
      "title",
      "image_url",
      "images",
      "price",
      "price_min",
      "sculptor",
      "vendor",
      "variants"
    ]
  });

  console.log("[Meilisearch] Response:", response);

  if (!response) {
    console.warn("[Meilisearch] No response, showing empty state");
    showEmpty(elements);
    return;
  }

  console.log("[Meilisearch] Found", response.hits.length, "results");
  renderSearchResults(response.hits, trimmedQuery, response.estimatedTotalHits, elements);
}

/**
 * Intercept native predictive search to prevent double fetches
 */
function interceptPredictiveSearch(input: HTMLInputElement): void {
  // Prevent the native predictive-search component from making requests
  const predictiveSearch = input.closest("predictive-search") as
    | (HTMLElement & {
        cachedResults?: unknown;
        getSearchResults?: (query: string) => void;
      })
    | null;

  if (predictiveSearch) {
    // Override the native fetch behavior by setting a custom attribute
    predictiveSearch.setAttribute("data-meili-override", "true");

    // Disable the native component's getSearchResults method if it exists
    if (typeof predictiveSearch.getSearchResults === "function") {
      predictiveSearch.getSearchResults = () => {
        // No-op - let Meilisearch handle it
      };
    }

    // Clear any cached results
    if (predictiveSearch.cachedResults) {
      predictiveSearch.cachedResults = {};
    }
  }
}

/**
 * Track initialized inputs and their cleanup functions
 */
const initializedInputs = new Map<string, () => void>();

/**
 * Clear initialized state for all inputs (used when modal reopens)
 */
export function clearInitializedInputs(): void {
  // Call cleanup functions for all inputs
  initializedInputs.forEach((cleanup) => cleanup());
  initializedInputs.clear();
  console.log("[Meilisearch] Cleared all initialized inputs");
}

/**
 * Initialize search preview for a given input
 */
export function initSearchPreview(
  inputSelector: string,
  previewSelector: string,
  context: Element | Document = document,
  force = false
): (() => void) | null {
  if (!isMeiliEnabled()) {
    console.log("[Meilisearch] Not enabled, skipping init");
    return null;
  }

  const input =
    context.querySelector<HTMLInputElement>(inputSelector) ||
    document.querySelector<HTMLInputElement>(inputSelector);

  if (!input) {
    console.warn("[Meilisearch] Input not found:", inputSelector);
    return null;
  }

  // Avoid double initialization unless forced
  if (initializedInputs.has(input.id) && !force) {
    console.log("[Meilisearch] Already initialized:", input.id);
    return null;
  }

  // Clean up previous initialization if forcing
  if (force && initializedInputs.has(input.id)) {
    const cleanup = initializedInputs.get(input.id);
    if (cleanup) cleanup();
    initializedInputs.delete(input.id);
    console.log("[Meilisearch] Cleaned up previous init for:", input.id);
  }

  // Find preview container relative to the input's form
  const form = input.closest("form");
  const previewContainer =
    form?.querySelector<HTMLElement>("[data-meili-search-preview]") ||
    context.querySelector<HTMLElement>("[data-meili-search-preview]") ||
    document.querySelector<HTMLElement>(previewSelector);

  if (!previewContainer) {
    console.warn("[Meilisearch] Preview container not found for:", input.id);
    return null;
  }

  const elements = getSearchElements(previewContainer);
  if (!elements) {
    console.warn("[Meilisearch] Missing preview elements for:", input.id);
    return null;
  }

  console.log("[Meilisearch] Successfully initialized for:", input.id);

  // Intercept native search
  interceptPredictiveSearch(input);

  // Debounced search handler
  const debouncedSearch = debounce((query: string) => {
    performSearch(query, elements);
  }, DEBOUNCE_DELAY);

  // Input handler
  const handleInput = (e: Event) => {
    const target = e.target as HTMLInputElement;
    debouncedSearch(target.value);
  };

  // Focus handler - show preview if there's a query
  const handleFocus = () => {
    if (input.value.trim().length >= MIN_QUERY_LENGTH) {
      elements.container.hidden = false;
    }
  };

  // Blur handler - hide preview with delay (for click events)
  const handleBlur = () => {
    setTimeout(() => {
      hidePreview(elements);
    }, 200);
  };

  // Keyboard navigation
  const handleKeydown = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      hidePreview(elements);
      input.blur();
    }

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const items = elements.results.querySelectorAll<HTMLElement>(".meili-search-preview__item");
      if (items.length === 0) return;

      const focused = elements.results.querySelector<HTMLElement>(
        ".meili-search-preview__item--focused"
      );
      let nextIndex = 0;

      if (focused) {
        focused.classList.remove("meili-search-preview__item--focused");
        const currentIndex = Array.from(items).indexOf(focused);
        if (e.key === "ArrowDown") {
          nextIndex = (currentIndex + 1) % items.length;
        } else {
          nextIndex = currentIndex - 1 < 0 ? items.length - 1 : currentIndex - 1;
        }
      }

      items[nextIndex].classList.add("meili-search-preview__item--focused");
      const link = items[nextIndex].querySelector<HTMLAnchorElement>("a");
      link?.focus();
    }

    if (e.key === "Enter") {
      const focused = elements.results.querySelector<HTMLAnchorElement>(
        ".meili-search-preview__item--focused a"
      );
      if (focused) {
        e.preventDefault();
        focused.click();
      }
    }
  };

  // Attach event listeners
  input.addEventListener("input", handleInput);
  input.addEventListener("focus", handleFocus);
  input.addEventListener("blur", handleBlur);
  input.addEventListener("keydown", handleKeydown);

  // Create cleanup function
  const cleanup = () => {
    input.removeEventListener("input", handleInput);
    input.removeEventListener("focus", handleFocus);
    input.removeEventListener("blur", handleBlur);
    input.removeEventListener("keydown", handleKeydown);
  };

  // Store cleanup function for this input
  initializedInputs.set(input.id, cleanup);

  return cleanup;
}
